
import React from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import MpesaStatus from "./MpesaStatus";
import { PaymentStatus } from "./types";

export const mpesaFormSchema = z.object({
  phoneNumber: z.string()
    .min(9, "Phone number must be at least 9 digits")
    .regex(/^\d+$/, "Phone number must contain only digits")
});

type MpesaPaymentProps = {
  mpesaStatus: PaymentStatus;
  statusCheckCount: number;
  isProcessing: boolean;
  defaultPhoneNumber: string;
  onSubmit: (phoneNumber: string) => Promise<void>;
  onRetry: () => void;
  onContinue: () => void;
};

const MpesaPaymentForm: React.FC<MpesaPaymentProps> = ({
  mpesaStatus,
  statusCheckCount,
  isProcessing,
  defaultPhoneNumber,
  onSubmit,
  onRetry,
  onContinue
}) => {
  const mpesaForm = useForm<z.infer<typeof mpesaFormSchema>>({
    resolver: zodResolver(mpesaFormSchema),
    defaultValues: {
      phoneNumber: defaultPhoneNumber || "",
    },
  });

  const handleMpesaSubmit = async (values: z.infer<typeof mpesaFormSchema>) => {
    await onSubmit(values.phoneNumber);
  };

  if (mpesaStatus) {
    return (
      <MpesaStatus
        status={mpesaStatus}
        onRetry={onRetry}
        onContinue={onContinue}
        statusCheckCount={statusCheckCount}
        isProcessing={isProcessing}
      />
    );
  }

  return (
    <Form {...mpesaForm}>
      <form onSubmit={mpesaForm.handleSubmit(handleMpesaSubmit)} className="space-y-6 py-4">
        <FormField
          control={mpesaForm.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Enter your M-Pesa phone number" 
                  {...field} 
                  disabled={isProcessing}
                />
              </FormControl>
              <FormMessage />
              <p className="text-xs text-gray-500 mt-1">
                Format: 7XXXXXXXX or 07XXXXXXXX or 2547XXXXXXXX
              </p>
            </FormItem>
          )}
        />
        
        <Button 
          type="submit" 
          className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 text-white" 
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing
            </>
          ) : (
            "Pay with M-Pesa"
          )}
        </Button>
      </form>
    </Form>
  );
};

export default MpesaPaymentForm;
